import log from '../../logger';
import { activeInstance } from '../api/instance';
import { checkSnapshotIsComputedForInstance, getPreviewConfig } from '../api/contentApi';

/**
 * The app preview window.
 *
 * A port of Angular's `previewService`. It is module state rather than a hook because a popup
 * window outlives any component: the admin opens the preview, navigates around the CMS for ten
 * minutes, and the same window has to keep reloading. The Angular version was a singleton
 * service for the same reason.
 *
 * The window is `/appPreview.html`, which defines `setPreviewPath`, `reload`, `showSpinner`
 * and `hideSpinner` on itself. Those functions do not exist until it has loaded, which is why
 * `initPreviewWindow` retries on a timer rather than assuming.
 */
let previewWindow;
let currentUrl;
let currentInstance;

export function previewWindowIsValid() {
  return !!(previewWindow && previewWindow.Location && !previewWindow.closed);
}

export function closePreviewWindow() {
  if (previewWindowIsValid()) previewWindow.close();
}

/**
 * Block until the instance's snapshot has been computed, so the preview shows the content
 * that was just published rather than the previous snapshot.
 *
 * Polls once a second with no ceiling, exactly as the Angular version did. That is a
 * deliberate carry-over and not an endorsement: if snapshot computation stalls, this spins
 * forever behind a spinner. Worth a timeout, but changing it inside a migration would be a
 * behaviour change disguised as a port.
 */
async function waitForPreviewSnapshot() {
  let isComputed = await checkSnapshotIsComputedForInstance(currentInstance);
  while (!isComputed) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    isComputed = await checkSnapshotIsComputedForInstance(currentInstance);
  }
}

async function ensurePreviewReady(target, checkSnapshot) {
  if (!checkSnapshot) {
    target.hideSpinner?.();
    return;
  }
  target.showSpinner?.();
  await waitForPreviewSnapshot();
  setTimeout(() => target.hideSpinner?.(), 250);
}

/**
 * How many times `initPreviewWindow` will wait for the popup to define its own API.
 *
 * The Angular version read `previewWindow.setPreviewPath` off a bare reference, so a blocked
 * popup threw once and the retry loop stopped. Writing that as `previewWindow?.setPreviewPath`
 * turned a hard stop into a permanent one-request-per-second loop, because the retry sat
 * inside `getPreviewConfig().then(...)` and re-fetched the config on every tick. A bound is
 * the honest fix: the popup either comes up in a few seconds or it was blocked.
 */
const PREVIEW_READY_ATTEMPTS = 10;

export function initPreviewWindow(manualUrl, scheme, overrideInstance, attempt = 0) {
  if (!previewWindow) {
    log.error('The preview window did not open. It was most likely blocked by the browser.');
    return;
  }
  getPreviewConfig(scheme).then((previewConfig) => {
    const url = manualUrl || previewConfig.url;
    if (!previewWindow.setPreviewPath) {
      if (attempt >= PREVIEW_READY_ATTEMPTS) {
        log.error('The preview window never finished loading; giving up.');
        return;
      }
      setTimeout(() => initPreviewWindow(url, scheme, overrideInstance, attempt + 1), 1000);
      return;
    }
    currentUrl = url;
    currentInstance = overrideInstance || activeInstance().id;
    ensurePreviewReady(previewWindow, previewConfig.checkSnapshot).then(() => {
      previewWindow.setPreviewPath(url);
    });
  });
}

export async function launchPreview(manualUrl, scheme, overrideInstance) {
  const previewConfig = await getPreviewConfig(scheme);
  const url = manualUrl || previewConfig.url;
  // A different target means the open window is showing the wrong thing, so it is closed
  // and reopened rather than redirected.
  if (url !== currentUrl) closePreviewWindow();
  if (previewWindowIsValid()) {
    // Focus first, then reload. `ensurePreviewReady` can block for as long as the snapshot
    // takes to compute, and the Angular version called `focus()` synchronously for exactly
    // that reason: otherwise clicking Preview App on an already-open window looks like it
    // did nothing until the poll finishes.
    previewWindow.focus();
    await ensurePreviewReady(previewWindow, previewConfig.checkSnapshot);
    previewWindow.reload();
    return;
  }
  previewWindow = window.open('/appPreview.html', undefined, 'height=800, width=680');
  initPreviewWindow(url, scheme, overrideInstance);
}

/**
 * Refresh the preview after a content change.
 *
 * Skipped on `live`, because live content only changes on publish and a reload there would
 * poll for a snapshot that is not being computed.
 */
export async function reloadPreview() {
  if (currentInstance === 'live' || !previewWindowIsValid()) return;
  await ensurePreviewReady(previewWindow, true);
  previewWindow.reload();
}

/** Test seam. Nothing in the app calls this. */
export function __resetPreviewState() {
  previewWindow = undefined;
  currentUrl = undefined;
  currentInstance = undefined;
}
